import {
  discoveryApiRef,
  fetchApiRef,
  githubAuthApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import { useCallback } from 'react';

export type FeedbackType = 'bug' | 'enhancement' | 'question';

export interface FeedbackInput {
  type: FeedbackType;
  title: string;
  body: string;
  /** Where the user was when they opened the form (recorded in the issue). */
  pageUrl: string;
}

export interface FeedbackResult {
  /** The created GitHub issue's html_url. */
  issueUrl: string;
  /** The created issue number. */
  number: number;
}

/**
 * Calls the `feedback` backend plugin to open a GitHub issue. The issue is
 * created **as the signed-in user** — their GitHub OAuth token (with `repo`
 * scope) is fetched here and forwarded, so they are the real author. The
 * first submission opens the GitHub authorization popup if the scope hasn't
 * been granted yet.
 */
export function useFeedbackApi() {
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);
  const githubAuth = useApi(githubAuthApiRef);

  const submit = useCallback(
    async (input: FeedbackInput): Promise<FeedbackResult> => {
      // Non-optional → opens the OAuth popup when `repo` hasn't been granted.
      const token = await githubAuth.getAccessToken(['repo']);
      if (!token) {
        throw new Error('GitHub authorization is required to send feedback.');
      }
      const baseUrl = await discoveryApi.getBaseUrl('feedback');
      const res = await fetchApi.fetch(`${baseUrl}/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-GitHub-Token': token,
        },
        body: JSON.stringify(input),
      });
      if (!res.ok) {
        // Surface the backend's readable message when present, else the status.
        let message = `Feedback submission failed (${res.status})`;
        try {
          const data = (await res.json()) as { error?: string };
          if (data?.error) message = data.error;
        } catch {
          // keep the default message
        }
        throw new Error(message);
      }
      return (await res.json()) as FeedbackResult;
    },
    [discoveryApi, fetchApi, githubAuth],
  );

  return { submit };
}
